import { useCallback, useState } from 'react';
import { HiOutlineEye } from 'react-icons/hi2';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import { ActionIcon, Button, Switch, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { menuRoutes } from '../../../router/menu';
import { RankingService } from '../../../services/ranking/rankingService';
import { RankingsApiResponse } from '../../../type/entities/ranking-type';
import RankingModalForm from './RankingModalForm';

const columnOrder = [
    'id',
    'name',
    'description',
    'resetInterval',
    'resetIntervalCustomDates',
    'resetIntervalCustomStartDate',
    'resetIntervalCustomEndDate',
    'active',
];

const RankingsList = () => {

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const rankingService = new RankingService();

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRanking, setSelectedRanking] = useState<any>({
        name: "",
        description: null,
        resetInterval: "",
        resetIntervalCustomStartDate: new Date().toISOString().split('T')[0],
        resetIntervalCustomEndDate: new Date(new Date().setDate(new Date().getDate() + 14)).toISOString().split('T')[0]
    });

    const [rankings, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await rankingService.listRankings(filters);
        return response.getResponseData() as RankingsApiResponse;
    }, [filters]));

    const handleToggleStatus = useCallback(async (id: string) => {
        if (!userCan('edit_rankings', 'rankings')) {
            toast.error('No tienes permisos para actualizar estados');
            return;
        }
        try {
            await rankingService.toggleRankingState(id);
            toast.success('Estado del ranking actualizado correctamente');
            refetch();
        } catch (error: any) {
            handleErrors(error);
        }
    }, [rankingService, refetch, handleErrors]);

    const handleDeleteRanking = useCallback(async (id: string) => {
        try {
            await rankingService.deleteRanking(id);
            toast.success('Ranking eliminado correctamente');
            refetch();
        } catch (error: any) {
            handleErrors(error);
        }
    }, [rankingService, refetch, handleErrors]);

    const handleDeleteMultipleRankings = useCallback(async (ids: string[]) => {
        try {
            await rankingService.deleteMultipleRankings(ids);
            toast.success('Rankings eliminados correctamente');
            refetch();
        } catch (error: any) {
            handleErrors(error);
        }
    }, [rankingService, refetch, handleErrors]);

    const handleEditRanking = useCallback((ranking: any) => {
        setSelectedRanking({
            id: ranking.id,
            name: ranking.name,
            description: ranking.description,
            resetInterval: ranking.resetInterval,
            resetIntervalCustomStartDate: ranking.resetIntervalCustomStartDate?.date,
            resetIntervalCustomEndDate: ranking.resetIntervalCustomEndDate?.date,
        });
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedRanking(null);
    }, []);

    const getResetIntervalLabel = (interval: string) => {
        const labels: { [key: string]: string } = {
            'daily': 'Diario',
            'weekly': 'Semanal',
            'monthly': 'Mensual',
            'custom': 'Personalizado',
        };
        return labels[interval] || interval;
    };

    /**
     * Order the data to match the column order
     */
    const orderedData = () => rankings?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    return (
        <div className="space-y-6">
            {rankings !== undefined && (
                <CustomTable
                    largeTable
                    id={'rankings-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={['resetIntervalCustomStartDate', 'resetIntervalCustomEndDate']}
                    overrideColumns={[
                        {
                            key: 'name',
                            render: (row: any) => {
                                return (
                                    userCan('get_rankings', 'rankings') && userCan('edit_rankings', 'rankings')
                                        ? (
                                            <Link to={`${menuRoutes.userRankings.path}/${row.row.original.id}/info`}>
                                                <Text className='font-bold text-primary'>{row.row.original.name}</Text>
                                            </Link>
                                        )
                                        : <Text className='font-bold text-primary'>{row.row.original.name}</Text>
                                );
                            },
                        },
                        {
                            key: 'description',
                            render: (row: any) => {
                                return (
                                    <div className="text-sm text-gray-600 max-w-xs truncate" title={row.row.original.description}>
                                        {row.row.original.description || 'Sin descripción'}
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'resetInterval',
                            render: (row: any) => {
                                return (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {getResetIntervalLabel(row.row.original.resetInterval)}
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'resetIntervalCustomDates',
                            render: (row: any) => {
                                return (
                                    <div className="text-sm text-gray-600">
                                        {row.row.original.resetInterval === 'custom' ? (
                                            <>
                                                {row.row.original.resetIntervalCustomStartDate ? new Date(row.row.original.resetIntervalCustomStartDate?.date).toLocaleDateString() : ''}
                                                {' - '}
                                                {row.row.original.resetIntervalCustomEndDate ? new Date(row.row.original.resetIntervalCustomEndDate?.date).toLocaleDateString() : ''}
                                            </>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'active',
                            render: (row: any) => {
                                return (
                                    <div className="flex items-center">
                                        <Switch
                                            checked={row.row.original.active}
                                            onChange={() => handleToggleStatus(row.row.original.id)}
                                        />
                                        <span className={`ml-2 text-xs ${row.row.original.active ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                            {row.row.original.active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </div>
                                );
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'rankings', permission: 'get_rankings' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver ranking'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => navigate(menuRoutes.userRankings.path + `/${row.id}/info`)}
                                        >
                                            <HiOutlineEye className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'rankings', permission: 'edit_rankings' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar ranking'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => handleEditRanking(row)}
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'rankings', permission: 'delete_rankings' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar ranking`}
                                                description={`¿Estás seguro de que deseas eliminar el ranking "${row.name}"?`}
                                                onDelete={() => handleDeleteRanking(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleDeleteMultipleRankings}
                    filter={false}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: rankings ? rankings.lastPage : 1,
                        totalCount: rankings?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                >
                    <Button
                        className="ml-2"
                        onClick={() => setIsModalOpen(true)}
                    >
                        Crear nuevo ranking
                    </Button>
                </CustomTable>
            )}

            <RankingModalForm
                data={selectedRanking}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                refetch={refetch}
            />
        </div>
    );
};

export default RankingsList;